import {
  eur,
  pctFmt,
  eurExact,
  txnsFmt,
  monthLong,
} from "./components/shared/formatters";
import { PriceSpec, BREAKDOWN_COLORS } from "./pricingData";

/**
 * Single source of truth for the formulas behind every "Projected net take"
 * figure. Each builder returns the symbolic expression, the exact variables
 * (with their live values) that were plugged in, and the formatted result so
 * the UI can render a fully explainable, double-checkable breakdown.
 */

export interface FormulaInput {
  label: string;
  value: string;
}

export interface FormulaExplanation {
  /** Symbolic formula, e.g. "Net take ÷ Volume × 100". */
  expression: string;
  /** Exact variables (with live values) plugged into the formula. */
  inputs: FormulaInput[];
  /** Formatted final result. */
  result: string;
  /** Optional clarifying note. */
  note?: string;
}

// Effective rate = net take as a percentage of processed volume.
export function effectiveRateFormula(
  totalNet: number,
  tpv: number
): FormulaExplanation {
  const rate = tpv > 0 ? (totalNet / tpv) * 100 : 0;
  return {
    expression: "Effective rate = Net take ÷ Volume × 100",
    inputs: [
      { label: "Net take (monthly)", value: eur(totalNet, 2) },
      { label: "Volume / TPV (monthly)", value: eur(tpv, 2) },
    ],
    result: pctFmt(rate, 3),
    note: "Blended take-rate across all pricing groups for the modelled month.",
  };
}

// ── Monthly net take: per-pricing-group breakdown ──
// Net take is computed group-by-group (proposed price × that group's slice of
// volume or transactions, minus the same for processing cost), then summed.

interface ScenarioRowLike {
  def: { label: string; cost: PriceSpec };
  cost: PriceSpec;
  price: PriceSpec;
  volShare: number;
  txnShare: number;
  revAmt: number;
  costAmt: number;
  net: number;
}

/** A basis figure that belongs to one pricing group, plus how it relates to the
 *  business-wide total (e.g. value "€69,784", note "14.8% of €470,016 total"). */
export interface GroupBasis {
  value: string;
  note: string;
}

export interface GroupNetRow {
  label: string;
  color: string;
  /** This group's volume and how it relates to the business total. */
  volumeBasis?: GroupBasis;
  /** This group's transaction count and how it relates to the business total. */
  txnBasis?: GroupBasis;
  /** e.g. "1.79% × €120,000" — proposed price applied to this group's volume/txns. */
  revExpr: string;
  rev: number;
  /** e.g. "1.20% × €120,000" — processing cost on the same basis. */
  costExpr: string;
  cost: number;
  net: number;
}

export interface MonthlyNetExplanation {
  expression: string;
  groups: GroupNetRow[];
  totalRev: number;
  totalCost: number;
  totalNet: number;
}

function priceLabel(p: PriceSpec): string {
  return p.valueType === "Percentage" ? `${p.value}%` : eurExact(p.value);
}

export function monthlyNetGroupBreakdown(
  rows: ScenarioRowLike[],
  tpv: number,
  txns: number
): MonthlyNetExplanation {
  const fmtPct = (n: number) => `${n.toFixed(1)}%`;

  const groups: GroupNetRow[] = rows.map((r, i) => {
    const gTPV = (tpv * r.volShare) / 100;
    const gTxns = (txns * r.txnShare) / 100;
    const basisFor = (p: PriceSpec) =>
      p.valueType === "Percentage" ? eur(gTPV) : `${txnsFmt(gTxns)} txns`;
    const usesVolume =
      r.price.valueType === "Percentage" || r.cost.valueType === "Percentage";
    const usesTxns =
      r.price.valueType === "Fixed" || r.cost.valueType === "Fixed";
    return {
      label: r.def.label,
      color: BREAKDOWN_COLORS[i % BREAKDOWN_COLORS.length],
      volumeBasis: usesVolume
        ? {
            value: eur(gTPV),
            note: `${fmtPct(r.volShare)} of ${eur(tpv)} business total`,
          }
        : undefined,
      txnBasis: usesTxns
        ? {
            value: `${txnsFmt(gTxns)} txns`,
            note: `${fmtPct(r.txnShare)} of ${txnsFmt(txns)} business total`,
          }
        : undefined,
      revExpr: `${priceLabel(r.price)} × ${basisFor(r.price)}`,
      rev: r.revAmt,
      costExpr: `${priceLabel(r.cost)} × ${basisFor(r.cost)}`,
      cost: r.costAmt,
      net: r.net,
    };
  });
  const totalRev = rows.reduce((a, r) => a + r.revAmt, 0);
  const totalCost = rows.reduce((a, r) => a + r.costAmt, 0);
  return {
    expression: "Σ (group revenue − group cost)",
    groups,
    totalRev,
    totalCost,
    totalNet: totalRev - totalCost,
  };
}

export interface AnnualNetBreakdown {
  annual: number;
  monthCount: number;
  /** True when summed from real per-month history; false when projected from a single month. */
  perMonth: boolean;
  avgMonthlyNet: number;
  /** Set (e.g. "2026-01") when the figure annualizes a specific selected month. */
  selMonth?: string | null;
  /** True when one of the months counted is still in progress, so its
   * contribution is a rate projected from the days so far. */
  projected?: boolean;
}

// Annual net take = full-year net. Annualizes the selected month when one is
// picked, otherwise sums real per-month history (or projects the average month).
export function annualNetFormula({
  annual,
  monthCount,
  perMonth,
  avgMonthlyNet,
  selMonth,
  projected,
}: AnnualNetBreakdown): FormulaExplanation {
  if (selMonth) {
    const label = monthLong(selMonth);
    return {
      expression: "Annual net take = Selected month net × 12",
      inputs: [
        { label: `${label} net`, value: eur(avgMonthlyNet, 2) },
        { label: "Months / year", value: "× 12" },
      ],
      result: eur(annual, 2),
      note: `Projects ${label} across a full year. Clear the selected month to see the whole selected period instead.`,
    };
  }
  if (perMonth) {
    const fullYear = monthCount >= 12;
    return {
      expression: fullYear
        ? "Annual net take = Σ each month's net take"
        : `Annual net take = Σ each month's net take × 12 / ${monthCount}`,
      inputs: [
        { label: "Months counted", value: String(monthCount) },
        { label: "Avg month net", value: eur(avgMonthlyNet, 2) },
      ],
      result: eur(annual, 2),
      note:
        (fullYear
          ? `Sum of all ${monthCount} months of history, applying current pricing to each month's real volume & transactions.`
          : `Current pricing is applied to each of the ${monthCount} months in the selected period, then scaled to a full year. Only ${monthCount} months were measured, so the annual figure is extrapolated from them.`) +
        (projected
          ? " The last month is still in progress; its takings so far are scaled to a full-month rate, so part of this total is a projection."
          : "") +
        " A single month can never exceed this total.",
    };
  }
  return {
    expression: "Annual net take = Avg month net × 12",
    inputs: [
      { label: "Avg month net", value: eur(avgMonthlyNet, 2) },
      { label: "Months / year", value: "× 12" },
    ],
    result: eur(annual, 2),
    note: "No per-month history available, so the average month is projected across 12 months.",
  };
}
